/* Uit Krabbelpaal.dc.html; bodies letterlijk overgenomen. */

import { BLANK_PNG, GH, GW, MAX_FRAMES } from '../lib/config.js';
import { toPngCanvas } from '../lib/pixels.js';
import { playBlip, playPop } from '../lib/sound.js';
import { frameMs } from '../lib/text.js';
import * as store from '../lib/store.js';

export default {
  canvasDataUrl() {
      const cv = this.canvasRef.current;
      try { return cv ? cv.toDataURL('image/png') : ''; } catch (e) { return ''; }
    },

  drawDataUrl(url) {
      const ctx = this.getCtx();
      return new Promise((done) => {
        if (!ctx) { done(); return; }
        ctx.clearRect(0, 0, GW, GH);
        if (!url) { done(); return; }
        const im = new Image();
        im.onload = () => { ctx.imageSmoothingEnabled = false; ctx.drawImage(im, 0, 0, GW, GH); done(); };
        im.onerror = () => done();
        im.src = url;
      });
    },

  // Beeld 1 bewaart zich als gewoon werk-in-uitvoering; alles daarna hoort bij
  // elkaar en gaat als rijtje naar plek 2, zowel lokaal als op de server.
  parseExtra(raw) {
      const s = String(raw || '');
      const art = ['', '', '', '', ''];
      const on = [false, false, false, false, false];
      if (!s) return { art, on };
      if (s.charAt(0) === '{') {
        try {
          const o = JSON.parse(s) || {};
          const a = Array.isArray(o.a) ? o.a : [];
          const f = Array.isArray(o.on) ? o.on : [];
          return {
            art: art.map((_, i) => typeof a[i] === 'string' ? a[i] : ''),
            on: on.map((_, i) => !!f[i])
          };
        } catch (e) { return { art, on }; }
      }
      // Oudere vorm: één beeld, of een rijtje dat helemaal aan stond.
      let list = [s];
      if (s.charAt(0) === '[') {
        try { const a = JSON.parse(s); list = Array.isArray(a) ? a : []; } catch (e) { list = []; }
      }
      return {
        art: art.map((_, i) => typeof list[i] === 'string' ? list[i] : ''),
        on: on.map((_, i) => i < list.length)
      };
    },

  encodeExtra(frames, on) {
      const a = [], f = [];
      for (let i = 1; i < MAX_FRAMES; i++) { a.push((frames || [])[i] || ''); f.push(!!(on || [])[i]); }
      if (!f.some(Boolean) && !a.some(Boolean)) return '';
      return JSON.stringify({ a, on: f });
    },

  stashExtra(frames, on, now) {
      const s = this.encodeExtra(frames, on);
      try {
        if (s) store.set('gp_wip_b', s); else store.remove('gp_wip_b');
        store.set(this.slotKey(2), s);
      } catch (e) {}
      clearTimeout(this.extraCloudTimer);
      if (now) { this.saveProject(2, s); return; }
      this.extraCloudTimer = setTimeout(() => this.saveProject(2, s), 1200);
    },

  stashFirst(url) {
      try {
        if (url) store.set('gp_wip', url); else store.remove('gp_wip');
        store.set('gp_wip_at', String(Date.now()));
        store.set(this.slotKey(this.state.slot || 0), url || '');
      } catch (e) {}
      this.wipDirty = null;
      clearTimeout(this.wipCloudTimer);
      this.saveProject(this.state.slot || 0, url || '');
    },

  emptyFrames() { return ['', '', '', '', '', '']; },

  emptyOn() { return [true, false, false, false, false, false]; },

  framesArr() { const f = this.state.frames || []; return this.emptyFrames().map((_, i) => f[i] || ''); },

  onArr() { const o = this.state.frameOn || []; return this.emptyOn().map((_, i) => i === 0 ? true : !!o[i]); },

  activeList() { return this.onArr().map((v, i) => v ? i : -1).filter((i) => i >= 0); },

  /**
   * Hoe groot een beeldvakje is: zo dat de rij precies zo breed is als het doek.
   *
   * Zeven vakjes (de playknop en zes beelden) met zes gaten van acht, over de breedte
   * van het doek plus zijn rand van vier aan elke kant. Hier stond een bovengrens van
   * 52 en een som zonder die rand, en dan was de rij onder een doek van 456 maar 412
   * breed: 22 pixels te kort aan elke kant. Nu is de bovengrens 58, de maat van de
   * knoppen links en rechts van het doek, en nooit kleiner dan een fijne kliktegel.
   *
   * De gaten blijven acht, want het slepen van een beeldje rekent met `frameSide() + 8`.
   */
  frameSide() {
      const px = this.state.canvasPx || 320;
      return Math.max(40, Math.min(58, Math.floor((px + 8 - 6 * 8) / 7)));
    },

  animOn() { return this.activeList().length > 1; },

  frameCount() { return this.activeList().length; },

  canvasEmpty() {
      const cv = this.canvasRef.current;
      if (!cv) return true;
      const d = cv.getContext('2d').getImageData(0, 0, GW, GH).data;
      for (let i = 3; i < d.length; i += 4) if (d[i]) return false;
      return true;
    },

  // Het doek zoals het er nu staat, of leeg als er geen pixel op zit.
  frameNow() { return this.canvasEmpty() ? '' : this.canvasDataUrl(); },

  // Het beeld dat in de rij vóór dit beeld komt; dat schemert erdoorheen.
  onionArt() {
      const act = this.activeList();
      if (act.length < 2) return '';
      const at = act.indexOf(this.state.frameIdx || 0);
      if (at < 0) return '';
      return this.framesArr()[act[(at - 1 + act.length) % act.length]] || '';
    },

  // Naar een ander beeld: eerst dit beeld op zijn plek, dan het andere ophalen.
  async goFrame(i) {
      if (i < 0 || i >= MAX_FRAMES || !this.onArr()[i] || i === (this.state.frameIdx || 0)) return;
      // Eén wissel tegelijk: anders kan een late wissel het beeld van een eerdere
      // op de verkeerde plek wegschrijven.
      if (this.frameBusy) return;
      this.frameBusy = true;
      try {
        // De wachtende opslag afbreken: die zou het verkeerde beeld wegschrijven.
        clearTimeout(this.wipTimer);
        const frames = this.framesArr();
        const from = this.state.frameIdx || 0;
        frames[from] = this.frameNow();
        if (from === 0) this.stashFirst(frames[0]); else this.stashExtra(frames, this.onArr(), true);
        await this.drawDataUrl(frames[i] || '');
        // De geschiedenis blijft staan: ongedaan maken werkt over de beelden heen.
        this.opBefore = null;
        this.clearSel();
        await new Promise((done) => this.setState({ frames, frameIdx: i }, () => { this.snapshotCanvas(); done(); }));
        playBlip();
      } finally {
        this.frameBusy = false;
      }
    },

  // Met de pijltjes naar het vorige of volgende beeld dat aan staat; aan het
  // eind kom je weer bij het begin uit.
  stepFrame(d) {
      const act = this.activeList();
      if (act.length < 2) return;
      const cur = this.state.frameIdx || 0;
      const at = act.indexOf(cur);
      const next = act[((at < 0 ? 0 : at) + d + act.length) % act.length];
      if (next === cur) return;
      this.stopPlay();
      this.goFrame(next);
    },

  // Een vakje aan- of uitzetten. Aanzetten neemt het beeld ervoor over, zodat
  // je alleen nog hoeft te verschuiven wat beweegt. Uitzetten bewaart het werk.
  async toggleFrame(i) {
      if (i <= 0 || i >= MAX_FRAMES || this.frameBusy) return;
      clearTimeout(this.wipTimer);
      const frames = this.framesArr();
      const on = this.onArr();
      const cur = this.state.frameIdx || 0;
      frames[cur] = this.frameNow();
      // Aan- en uitzetten is ook een stap: hij staat op de stapel tussen de streken,
      // zodat ongedaan maken precies dezelfde weg terug loopt.
      const undoBefore = { frames: frames.slice(), on: on.slice(), idx: cur };
      let next = cur;
      if (on[i]) {
        if (this.activeList().length <= 1) return;
        on[i] = false;
        // Alles achter een uitgezet vakje schuift niet mee: die blijven staan.
        if (cur === i) {
          const act = on.map((v, k) => v ? k : -1).filter((k) => k >= 0);
          const before = act.filter((k) => k < i);
          next = before.length ? before[before.length - 1] : act[0];
        }
        playPop();
      } else {
        const before = on.map((v, k) => v ? k : -1).filter((k) => k >= 0 && k < i);
        on[i] = true;
        if (!frames[i] && before.length) frames[i] = frames[before[before.length - 1]] || '';
        next = i;
        playBlip();
      }
      if (next !== cur) {
        // Dezelfde sluis als goFrame: een Cmd+Z of pijltje in dit gat liet de stapel
        // tussendoor lopen, en dan schreef de setState hieronder oude beelden terug.
        this.frameBusy = true;
        try { await this.drawDataUrl(frames[next] || ''); } finally { this.frameBusy = false; }
        this.opBefore = null;
        this.clearSel();
      }
      if (cur === 0 || next === 0) this.stashFirst(frames[0]);
      this.setState({ frames, frameOn: on, frameIdx: next, playing: false }, () => {
        this.stashExtra(frames, on, true);
        this.snapshotCanvas();
        this.pushDoc(undoBefore, { frames: frames.slice(), on: on.slice(), idx: next });
      });
    },

  // Afspelen in het grote doek: de beelden schuiven over het canvas heen.
  async togglePlay() {
      if (this.state.playing) { this.stopPlay(); return; }
      if (!this.animOn() || this.playPrep) return;
      this.finishPen();
      this.clearSel();
      playBlip();
      // Eerst alle beelden inladen en klaarzetten, dan pas starten: zo begint de
      // eerste ronde niet met een leeg of half beeld.
      this.playPrep = true;
      try {
        const fr = this.framesArr();
        fr[this.state.frameIdx || 0] = this.canvasDataUrl();
        const urls = this.activeList().map((i) => fr[i] || BLANK_PNG);
        if (urls.length < 2) return;
        this.playImgs = await Promise.all(urls.map((u) => new Promise((done) => {
          const im = new Image();
          im.onload = () => (im.decode ? im.decode().then(() => done(im), () => done(im)) : done(im));
          im.onerror = () => done(im);
          im.src = u;
        })));
        this.playUrls = urls;
        this.setState({ playing: true }, this.startPlayLoop);
      } finally {
        this.playPrep = false;
      }
    },

  // Zelf de klok bijhouden: elk beeld staat er even lang (8 per seconde, of 3
  // bij twee beelden) en er is altijd maar één plaatje, dus er kan niets uit de
  // maat lopen of dubbel in beeld staan.
  startPlayLoop() {
      const urls = this.playUrls || [];
      if (urls.length < 2) return;
      cancelAnimationFrame(this.playRaf);
      const el = this.playRef.current;
      if (el) el.src = urls[0];
      this.playAt = 0;
      const t0 = performance.now();
      const ms = frameMs(urls.length);
      const step = (t) => {
        if (!this.state.playing) return;
        const i = Math.floor((t - t0) / ms) % urls.length;
        if (i !== this.playAt) {
          this.playAt = i;
          const img = this.playRef.current;
          if (img) img.src = urls[i];
        }
        this.playRaf = requestAnimationFrame(step);
      };
      this.playRaf = requestAnimationFrame(step);
    },

  stopPlay() {
      cancelAnimationFrame(this.playRaf);
      this.playRaf = null;
      if (this.state.playing) this.setState({ playing: false });
    },

  // Beelden slepen: alleen de tekeningen wisselen van plek, de aan-vakjes
  // blijven staan waar ze staan.
  async moveFrame(from, to) {
      const on = this.onArr();
      if (from === to || !on[from] || !on[to] || this.frameBusy) return;
      const act = this.activeList();
      const fi = act.indexOf(from), ti = act.indexOf(to);
      if (fi < 0 || ti < 0) return;
      clearTimeout(this.wipTimer);
      const frames = this.framesArr();
      const cur = this.state.frameIdx || 0;
      frames[cur] = this.frameNow();
      const undoBefore = { frames: frames.slice(), on: on.slice(), idx: cur };
      const art = act.map((i) => frames[i] || '');
      const order = act.map((_, k) => k);
      const [movedArt] = art.splice(fi, 1); art.splice(ti, 0, movedArt);
      const [movedK] = order.splice(fi, 1); order.splice(ti, 0, movedK);
      act.forEach((slot, k) => { frames[slot] = art[k]; });
      const curAt = act.indexOf(cur);
      const next = curAt >= 0 ? act[order.indexOf(curAt)] : cur;
      this.stashFirst(frames[0]);
      if (next !== cur) {
        // Dezelfde sluis als goFrame: een Cmd+Z of pijltje in dit gat liet de stapel
        // tussendoor lopen, en dan schreef de setState hieronder oude beelden terug.
        this.frameBusy = true;
        try { await this.drawDataUrl(frames[next] || ''); } finally { this.frameBusy = false; }
        this.opBefore = null;
        this.clearSel();
      }
      this.setState({ frames, frameIdx: next, playing: false }, () => {
        this.stashExtra(frames, on, true);
        this.snapshotCanvas();
        this.pushDoc(undoBefore, { frames: frames.slice(), on: on.slice(), idx: next });
      });
      playBlip();
    },

  onFrameDragStart(i) { return (e) => {
      if (!this.onArr()[i]) { e.preventDefault(); return; }
      this.stopPlay();
      this.dragFrom = i;
      this.setState({ dragFrame: i, dragOver: null });
      try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (err) {}
    }; },

  onFrameDragOver(i) { return (e) => {
      if (this.dragFrom == null || !this.onArr()[i]) return;
      e.preventDefault();
      try { e.dataTransfer.dropEffect = 'move'; } catch (err) {}
      if (this.state.dragOver !== i) this.setState({ dragOver: i });
    }; },

  onFrameDragLeave(i) { return () => { if (this.state.dragOver === i) this.setState({ dragOver: null }); }; },

  onFrameDrop(i) { return async (e) => {
      e.preventDefault();
      const from = this.dragFrom;
      this.dragFrom = null;
      if (from == null || from === i) { this.setState({ dragFrame: null, dragOver: null }); return; }
      // De beelden wisselen meteen van plek; elk vakje begint daarna even op de
      // oude plek en glijdt van daaruit naar zijn nieuwe, zodat je de verhuizing
      // pas na het loslaten ziet.
      const act = this.activeList();
      const fi = act.indexOf(from), ti = act.indexOf(i);
      if (fi >= 0 && ti >= 0) {
        const step = this.frameSide() + 8;
        const order = act.slice();
        const [m] = order.splice(fi, 1); order.splice(ti, 0, m);
        const flip = {};
        order.forEach((was, k) => { flip[act[k]] = (was - act[k]) * step; });
        this.flipShift = flip;
        this.flipInstant = true;
      }
      await this.moveFrame(from, i);
      this.setState({ dragFrame: null, dragOver: null }, () => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          this.flipInstant = false;
          this.flipShift = null;
          this.flipAnim = true;
          this.forceUpdate();
          clearTimeout(this.flipTimer);
          this.flipTimer = setTimeout(() => { this.flipAnim = false; this.forceUpdate(); }, 280);
        }));
      });
    }; },

  onFrameDragEnd() {
      this.dragFrom = null;
      if (this.state.dragFrame != null || this.state.dragOver != null) this.setState({ dragFrame: null, dragOver: null });
    },

  // De beelden die aan staan, naast elkaar in één strook van 32 hoog.
  buildStripBlob() {
      const frames = this.framesArr();
      frames[this.state.frameIdx || 0] = this.canvasDataUrl();
      const list = this.activeList().map((i) => frames[i] || '');
      const n = Math.max(1, list.length);
      const out = document.createElement('canvas');
      out.width = GW * n; out.height = GH;
      const c = out.getContext('2d');
      c.fillStyle = '#fff';
      c.fillRect(0, 0, GW * n, GH);
      c.imageSmoothingEnabled = false;
      const put = (url, x) => new Promise((done) => {
        if (!url) { done(); return; }
        const im = new Image();
        im.onload = () => { c.drawImage(im, x, 0, GW, GH); done(); };
        im.onerror = () => done();
        im.src = url;
      });
      return list.reduce((p, url, i) => p.then(() => put(url, GW * i)), Promise.resolve())
        .then(() => new Promise((r) => out.toBlob(r, 'image/png')));
    },

  // Nieuw begint helemaal opnieuw: alle beelden leeg, alleen het eerste vakje
  // staat weer aan. Eén keer ongedaan maken zet je hele krabbel terug.
  onNew() {
      const ctx = this.getCtx();
      if (!ctx) return;
      this.cancelPen();
      const before = this.snapDoc();
      ctx.clearRect(0, 0, GW, GH);
      this.clearSel();
      this.opBefore = null;
      if (this.state.duo) { this.duoQueueSend(); return; }
      const after = { frames: this.emptyFrames(), on: this.emptyOn(), idx: 0 };
      const empty = before.idx === 0 && !before.frames.some((f) => f) && !before.on.slice(1).some((v) => v);
      this.setState({ frames: this.emptyFrames(), frameOn: this.emptyOn(), frameIdx: 0, playing: false }, () => {
        this.stashExtra(this.emptyFrames(), this.emptyOn(), true);
        this.snapshotCanvas();
      });
      try { store.remove('gp_wip'); } catch (err) {}
      if (!empty) this.pushDoc(before, after);
    },

  buildPngBlob() {
      const canvas = toPngCanvas(this.getCtx().getImageData(0, 0, GW, GH));
      return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    }
};
